export const contractorsData = {
  industry: "Contractors",
  slug: "contractors",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Contractors | HeyIntake",
    description: "AI-powered intake forms for contractors and home service professionals. Pre-qualify jobs, collect project details, and stop driving to bad estimates.",
    keywords: [
      "contractor intake form",
      "construction lead qualification",
      "project estimate intake",
      "home services lead management",
      "contractor CRM software",
      "job qualification for contractors"
    ]
  },

  heroTitle: "Client Intake Forms for Contractors & Home Services",
  heroSubtitle: "Qualify jobs, collect project details, and book more profitable work.",
  heroDescription: "Whether you're a general contractor, electrician, plumber, roofer, or home improvement specialist, HeyIntake helps you capture job details and identify the best opportunities.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Contractors Need Smarter Job Qualification",
    intro: "Time is money in the contracting business. Every hour spent on bad estimates is an hour you could be working on a paying job.",
    paragraphs: [
      "The traditional way contractors handle leads is broken. Someone calls or fills out a form, you drive across town, spend an hour measuring and discussing, write up a detailed estimate — and then never hear from them again. Meanwhile, a serious customer with a $50,000 renovation project got your voicemail and called the next contractor on the list.",
      "HeyIntake changes this dynamic. Our AI-powered intake forms capture project details, photos, budget range, and timeline before you leave your current job site. Our adaptive scoring identifies serious buyers versus price shoppers, so you know which estimates are worth your time.",
      "For general contractors, plumbers, electricians, roofers, and home service professionals, this means fewer wasted trips, higher close rates, and more time spent on profitable work."
    ]
  },
  
  painPoints: [
    {
      title: "Driving to Bad Estimates",
      description: "Wasting time and fuel driving to quote jobs that are too small, out of scope, or won't close."
    },
    {
      title: "Incomplete Job Information",
      description: "Showing up to estimates without enough details to provide accurate quotes."
    },
    {
      title: "Price Shoppers",
      description: "Spending time on detailed estimates for customers who are just collecting quotes with no intent to hire."
    },
    {
      title: "Missed Details = Change Orders",
      description: "Jobs go over budget because important details weren't captured before the quote."
    },
    {
      title: "No Way to Prioritize",
      description: "Treating a $500 repair request the same as a $50,000 renovation inquiry."
    },
    {
      title: "Follow-Up Falls Through",
      description: "Good leads get lost in the shuffle when you're busy on job sites all day."
    }
  ],

  exampleFields: [
    {
      category: "Contact Information",
      fields: [
        "Name and phone number",
        "Property address",
        "Best time to call",
        "How did you hear about us?",
        "Homeowner or property manager?"
      ]
    },
    {
      category: "Project Details",
      fields: [
        "Type of work needed",
        "Detailed description of the job",
        "Approximate square footage",
        "Age of home/building",
        "Photos of the work area"
      ]
    },
    {
      category: "Timeline & Budget",
      fields: [
        "When do you need this completed?",
        "Is this an emergency?",
        "Budget range for the project",
        "Have you gotten other quotes?"
      ]
    },
    {
      category: "Property Access",
      fields: [
        "HOA approval needed?",
        "Permit requirements known?",
        "Best days for site visit",
        "Any access restrictions?"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Contractor Intake Form",
    description: "Here's what an effective contractor quote request form looks like:",
    fields: [
      { label: "What type of project?", type: "select", options: ["Kitchen remodel", "Bathroom remodel", "Addition/Extension", "Whole home renovation", "New construction", "Repairs/Maintenance", "Other"] },
      { label: "Describe the project", type: "textarea" },
      { label: "Property address", type: "text" },
      { label: "Project timeline", type: "select", options: ["ASAP", "1-3 months", "3-6 months", "6+ months", "Just planning"] },
      { label: "Budget range", type: "select", options: ["$5K-$15K", "$15K-$30K", "$30K-$75K", "$75K-$150K", "$150K+"] },
      { label: "Do you have permits?", type: "select", options: ["Yes", "No - need help", "Not required", "Not sure"] },
      { label: "Best time for site visit", type: "select", options: ["Weekday mornings", "Weekday afternoons", "Weekends", "Flexible"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Contractors",
    subtitle: "From lead to qualified estimate in three simple steps",
    steps: [
      {
        title: "Customers Submit Details",
        description: "Homeowners provide project info, photos, budget, and timeline through your intake form."
      },
      {
        title: "AI Identifies Best Jobs",
        description: "Our AI scores each lead based on project value, budget alignment, and buyer readiness."
      },
      {
        title: "Focus on Profitable Work",
        description: "Prioritize estimates for serious customers with real budgets and realistic timelines."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Job Qualification for Contractors",
    description: "HeyIntake's AI helps you identify high-value jobs and serious customers before you drive across town for an estimate.",
    factors: [
      "Project size and estimated value",
      "Timeline urgency and flexibility",
      "Budget range vs. scope of work",
      "Homeowner readiness to proceed",
      "Previous contractor quotes (price shopping signals)"
    ],
    exampleLead: {
      name: "The Anderson Family",
      context: "Kitchen Remodel • $45K Budget • Ready to Start",
      summary: "Full kitchen renovation, budget aligns with scope, financing approved, flexible timeline. Homeowners, not investors. Prioritize estimate scheduling.",
      score: 88
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Pre-Qualify Before You Drive",
      description: "Know if a job is worth the trip before you leave the current site."
    },
    {
      icon: "chart",
      title: "Estimate More Accurately",
      description: "Arrive at estimates with photos, measurements, and details already in hand."
    },
    {
      icon: "zap",
      title: "Respond to Emergencies Fast",
      description: "Get instant alerts when customers submit urgent service requests."
    },
    {
      icon: "clock",
      title: "Never Lose a Lead",
      description: "Built-in CRM tracks every inquiry so nothing falls through when you're on the job."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Interior Designers", path: "/intake-form-for-interior-designers" },
    { name: "Intake Forms for Real Estate Agents", path: "/intake-form-for-real-estate-agents" },
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" },
    { name: "Intake Forms for Accountants", path: "/intake-form-for-accountants" }
  ],

  ctaText: "Ready to Book More Profitable Jobs?"
};
